import { useEffect, useRef } from "react";

/**
 * The spine every reveal animation hangs off.
 *
 * Each animation is a short film with an unknown ending: it plays its
 * suspense for as long as it takes, and only when the round is really
 * over does it learn which yantra won and land on it. So each one is
 * written as one async sequence rather than a pile of timers, and this
 * runs it, cancels it cleanly when the reveal closes, and hands it the
 * two things it needs:
 *
 *   await ctx.sleep(ms)        - a wait that gives up if we unmount
 *   await ctx.waitForWinner()  - resolves with the winning index, once
 *                                the server is willing to say
 *
 * The winner is polled rather than pushed because it arrives as a prop;
 * a tenth of a second of slack is invisible inside a spin and costs
 * nothing next to the network round trip it is waiting on.
 */

const CANCELLED = Symbol("reveal-sequence-cancelled");

export default function useRevealSequence(run, winnerIndex, deps = []) {
  const winnerRef = useRef(winnerIndex);
  winnerRef.current = winnerIndex;

  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    let cancelled = false;
    const timers = new Set();

    const sleep = (ms) =>
      new Promise((resolve, reject) => {
        const t = setTimeout(() => {
          timers.delete(t);
          if (cancelled) reject(CANCELLED);
          else resolve();
        }, ms);
        timers.add(t);
      });

    const waitForWinner = async () => {
      while (winnerRef.current < 0) {
        await sleep(100);
      }
      return winnerRef.current;
    };

    const ctx = {
      sleep,
      waitForWinner,
      /** The winning index right now, or -1 while the round still runs. */
      winnerIndex: () => winnerRef.current,
      get cancelled() {
        return cancelled;
      },
    };

    Promise.resolve()
      .then(() => runRef.current(ctx))
      .catch((err) => {
        if (err !== CANCELLED) throw err;
      });

    return () => {
      cancelled = true;
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
